import React, { useState, useRef } from "react";
import PageHeader from "../components/PageHeader";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Container,
  Alert,
  CircularProgress,
  FormHelperText,
  FormControl
} from "@mui/material";
import sixthBg from '../../assets/centrum_assets/design/index/sixthBG.png';
import MapSection from "../pages/franchisesectioning/MapSection";
import axiosClient from "axiosClient";
import SuccessModal from "../components/SuccessModal"; 
import ReCAPTCHA from "react-google-recaptcha"; 

const Franchise = () => {
  // --- STATE ---
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    location: "",
    email: "",
    message: "",
    interest: "",
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState(null);
  
  // Modal State
  const [openModal, setOpenModal] = useState(false);

  // <--- RECAPTCHA REF ---
  const recaptchaRef = useRef(null);

  // --- HANDLERS ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "phone") {
        // Allow digits, plus sign, spaces, dashes, and parentheses
        // This regex allows typing common phone formats
        if (/^[0-9+\s()-]*$/.test(value)) {
            // Limit to 20 chars to accommodate long international formats
            if (value.length <= 20) {
                setFormData({ ...formData, [name]: value });
            }
        }
    } else {
        setFormData({ ...formData, [name]: value });
    }
    
    if (fieldErrors[name]) {
        setFieldErrors({ ...fieldErrors, [name]: null });
    }
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    // --- UPDATED PHONE VALIDATION ---
    // Strip non-digits to count the actual numbers
    const phoneDigits = formData.phone.replace(/[^0-9]/g, "");

    if (!formData.phone) {
        errors.phone = "Phone number is required.";
        isValid = false;
    } else if (phoneDigits.length < 7) {
        // 7 is usually the minimum for landlines (excluding area code)
        // This prevents users from just typing "123"
        errors.phone = "Please enter a valid phone number (at least 7 digits).";
        isValid = false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
        errors.email = "Email is required.";
        isValid = false;
    } else if (!emailRegex.test(formData.email)) {
        errors.email = "Please enter a valid email address.";
        isValid = false;
    }

    if (!formData.interest) {
        errors.interest = "Please select an area of interest.";
        isValid = false;
    }

    if (!formData.name.trim()) {
        errors.name = "Name is required.";
        isValid = false;
    }

    setFieldErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError(null);

    if (!validateForm()) return; 

    setLoading(true);

    try {
      // <--- 1. EXECUTE CAPTCHA ---
      const token = await recaptchaRef.current.executeAsync();

      const payload = {
        name: formData.name,
        phone_number: formData.phone,
        email: formData.email,
        location: formData.location,
        message: formData.message,
        wants_franchise: formData.interest === 'franchise',
        has_property: formData.interest === 'property',
        benefits_concern: formData.interest === 'benefits',
        recaptcha_token: token, // <--- 2. SEND TOKEN
      };

      await axiosClient.post('/franchise-inquiries', payload);
      
      setOpenModal(true);

      setFormData({
        name: "", phone: "", location: "", email: "", message: "", interest: "",
      });
      setFieldErrors({});
      
      // <--- 3. RESET CAPTCHA ---
      recaptchaRef.current.reset();

    } catch (err) {
      const serverMsg = err.response?.data?.message;
      if (err.response?.status === 422) {
        const firstError = Object.values(err.response.data.errors)[0][0];
        setGeneralError(`Submission failed: ${firstError}`);
      } else {
        setGeneralError(serverMsg || "An unknown error occurred. Please try again.");
      }
      // <--- RESET CAPTCHA ON ERROR ---
      recaptchaRef.current.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader title="FRANCHISE" pages={[]} />
      <Box
        sx={{
          backgroundImage: `url(${sixthBg})`,
          backgroundSize: "cover",
          px: { xs: 2, md: 6 },
          py: 8,
          }}
        >
        <Container maxWidth="lg">
          <Typography variant="h5" sx={{ mt: 5,mb: 1, fontWeight: "bold", color: "#00b5e2" }}>
            FRANCHISE ON US
          </Typography>

          <Typography variant="h2" fontWeight={800} sx={{ mb: 3, mt: 1, color: "#0a1a3b" }}>
            PARTNER WITH US
          </Typography>

          <Grid container spacing={6} alignItems="flex-start">
            <Grid item xs={12} md={4}>
              <Typography variant="h2" fontWeight={800} sx={{ mb: 2, mt: 1, color: "#0a1a3b" }}>
                PARTNERSHIPS. <br /> PERFORMANCE. <br /> PROGRESS.
              </Typography>
              <Typography sx={{ color: "#555", mb: 2, maxWidth: "90%", fontSize: "1.5rem" }}>
                Creating a better future takes teamwork, collaborations and partnerships. Together we can achieve more.
              </Typography>
              <Typography sx={{ color: "#555", maxWidth: "90%", fontSize: "1.5rem" }}>
                Be part of the Centrum Fuel Team, enter your information below to speak to a Business Development Officer.
              </Typography>
            </Grid>

            <Grid item xs={12} md={8}>
              <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
                {/* FORM FIELDS */}
                <Box sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, ml: {md: 5, lg: 0}, gap: 2 }}>
                  <TextField name="name" placeholder="Name" fullWidth value={formData.name} onChange={handleChange} variant="outlined" required disabled={loading} error={!!fieldErrors.name} helperText={fieldErrors.name} sx={{ "& .MuiInputBase-root": { backgroundColor: "#f5f7f9", borderRadius: "6px" } }} />

                  <TextField name="phone" placeholder="Phone Number" fullWidth value={formData.phone} onChange={handleChange} variant="outlined" required disabled={loading} error={!!fieldErrors.phone} helperText={fieldErrors.phone} sx={{ "& .MuiInputBase-root": { backgroundColor: "#f5f7f9", borderRadius: "6px" } }} />
                </Box>

                <Box sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, ml: {md: 5, lg: 0}, gap: 2 }}>
                  <TextField name="location" placeholder="Location" fullWidth value={formData.location} onChange={handleChange} variant="outlined" disabled={loading} sx={{ "& .MuiInputBase-root": { backgroundColor: "#f5f7f9", borderRadius: "6px" } }} />
                  <TextField name="email" placeholder="Email Address" type="email" fullWidth value={formData.email} onChange={handleChange} variant="outlined" required disabled={loading} error={!!fieldErrors.email} helperText={fieldErrors.email} sx={{ "& .MuiInputBase-root": { backgroundColor: "#f5f7f9", borderRadius: "6px" } }} />
                </Box>

                <FormControl error={!!fieldErrors.interest} sx={{ ml: {md: 5, lg: 0} }}>
                    <RadioGroup row name="interest" value={formData.interest} onChange={handleChange} sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-start", gap: 2, "& .MuiFormControlLabel-label": { fontSize: "1rem" } }}>
                        <FormControlLabel value="franchise" control={<Radio disabled={loading} />} label="I want to start a franchise" />
                        <FormControlLabel value="property" control={<Radio disabled={loading} />} label="I have a property" />
                        {/* <FormControlLabel value="benefits" control={<Radio disabled={loading} />} label="I have a benefits concern" /> */}
                    </RadioGroup>
                    {fieldErrors.interest && (<FormHelperText>{fieldErrors.interest}</FormHelperText>)}
                </FormControl>

                <TextField name="message" placeholder="Message" fullWidth multiline rows={10} value={formData.message} onChange={handleChange} disabled={loading} sx={{ "& .MuiInputBase-root": { backgroundColor: "#f5f7f9", borderRadius: "6px", ml: {md: 5, lg: 0} } }} />

                {generalError && (<Alert severity="error" sx={{ ml: {md: 5, lg: 0} }}>{generalError}</Alert>)}

                <Button type="submit" variant="contained" disabled={loading} sx={{ mt: 2, alignSelf: "flex-start", backgroundColor: "#00C4F0", borderRadius: "25px", px: 4, fontWeight: 450, letterSpacing: 2, textTransform: "uppercase", "&:hover": { backgroundColor: "#00a7d6" }, minWidth: 160, fontSize: '1.4rem' }}>
                   {loading ? <CircularProgress size={24} color="inherit" /> : "Send Message"}
                </Button>
                
                <Typography 
                  sx={{ 
                    mt: 3, 
                    color: "#555", 
                    fontSize: "1.25rem",
                    lineHeight: 1.5,
                    maxWidth: "400px",
                    alignSelf: "flex-start" 
                  }}
                >
                  This site is protected by reCAPTCHA and the Google&nbsp;
                  <a 
                    href="https://policies.google.com/privacy" 
                    target="_blank" 
                    rel="noreferrer" 
                    style={{ color: "#00C4F0", textDecoration: "none", fontWeight: "bold" }}
                  >
                    Privacy Policy
                  </a> 
                  &nbsp;and&nbsp;
                  <a 
                    href="https://policies.google.com/terms" 
                    target="_blank" 
                    rel="noreferrer" 
                    style={{ color: "#00C4F0", textDecoration: "none", fontWeight: "bold" }}
                  >
                    Terms of Service
                  </a> 
                  &nbsp;apply.
                </Typography>

              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Box mt={-5}><MapSection /></Box>

      {/* <--- RECAPTCHA COMPONENT ---> */}
      <ReCAPTCHA
          ref={recaptchaRef}
          size="invisible"
          sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
      />

      <SuccessModal 
        open={openModal} 
        onClose={() => setOpenModal(false)}
        title="Application Received!"
        message="Thank you for your interest in partnering with Centrum Fuel. Our team will review your details and contact you soon."
      />
    </>
  );
};

export default Franchise;